import { Copy, ExternalLink, Route } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';

import type { LatLng } from './location-picker-map';

/**
 * Qué se puede (y qué NO) abrir en apps de mapas externas por URL universal
 * —sin API key, promocionable a la app nativa en móvil—:
 *
 * - **Ninguna acepta una geometría arbitraria** (KML/KMZ, polilínea, GeoJSON).
 *   Google retiró la carga de KML por URL en 2015; Waze, Apple Maps y OSM
 *   nunca la tuvieron. La única app masiva que renderiza un KMZ completo es
 *   Google Earth, y solo abriendo el ARCHIVO (por eso el caller puede añadir
 *   una acción de descarga vía `children`).
 * - **Google Maps** es el único con ruta multipunto: `dir/?api=1` acepta
 *   origen + destino + hasta 9 waypoints (3 en navegador móvil). Traza
 *   direcciones PEGADAS A LA CARRETERA entre esos puntos — una aproximación
 *   del trazado, no la geometría exacta.
 * - **Waze** solo acepta un destino (`ll=`); **Apple Maps** origen/destino
 *   (`saddr`/`daddr`); **OSM** un marcador (`mlat`/`mlon`). Para ellos lo
 *   máximo honesto es el pin de `value`.
 */
const PROVEEDORES_MAPAS = [
  {
    id: 'google',
    nombre: 'Google Maps',
    construirUrl: ({ lat, lng }: LatLng) =>
      `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
  },
  {
    id: 'waze',
    nombre: 'Waze',
    construirUrl: ({ lat, lng }: LatLng) => `https://www.waze.com/ul?ll=${lat},${lng}&navigate=yes`,
  },
  {
    id: 'apple',
    nombre: 'Apple Maps',
    construirUrl: ({ lat, lng }: LatLng) => `https://maps.apple.com/?ll=${lat},${lng}`,
  },
  {
    id: 'osm',
    nombre: 'OpenStreetMap',
    construirUrl: ({ lat, lng }: LatLng) =>
      `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`,
  },
] as const;

/**
 * Límites documentados de waypoints de la URL de direcciones de Google Maps
 * (https://developers.google.com/maps/documentation/urls/get-started):
 * hasta 9 intermedios, pero solo 3 cuando el enlace se abre en un navegador
 * móvil.
 */
const MAX_WAYPOINTS_ESCRITORIO = 9;
const MAX_WAYPOINTS_MOVIL = 3;

function esNavegadorMovil(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

const formatearPunto = ({ lat, lng }: LatLng) => `${lat.toFixed(6)},${lng.toFixed(6)}`;

/**
 * URL de direcciones de Google Maps que recorre el trazado: origen y destino
 * son los extremos de la ruta y los waypoints se muestrean uniformemente a lo
 * largo de ella (hasta el máximo del dispositivo). Google une los puntos por
 * carretera, así que el resultado sigue el corredor vial aunque no reproduce
 * la geometría exacta del archivo.
 */
function construirUrlRutaGoogle(ruta: LatLng[]): string {
  const maxWaypoints = esNavegadorMovil() ? MAX_WAYPOINTS_MOVIL : MAX_WAYPOINTS_ESCRITORIO;
  const origen = ruta[0] as LatLng;
  const destino = ruta[ruta.length - 1] as LatLng;

  const indices = new Set<number>();
  for (let i = 1; i <= maxWaypoints; i += 1) {
    const indice = Math.round((i * (ruta.length - 1)) / (maxWaypoints + 1));
    if (indice > 0 && indice < ruta.length - 1) indices.add(indice);
  }
  const waypoints = [...indices].sort((a, b) => a - b).map((i) => ruta[i] as LatLng);

  const params = new URLSearchParams({
    api: '1',
    travelmode: 'driving',
    origin: formatearPunto(origen),
    destination: formatearPunto(destino),
  });
  if (waypoints.length > 0) {
    params.set('waypoints', waypoints.map(formatearPunto).join('|'));
  }
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

export interface AbrirEnMapasButtonProps {
  /** Punto a abrir en los proveedores que solo aceptan un pin. */
  value: LatLng;
  /**
   * Trazado ordenado (≥2 puntos). Si se provee, Google Maps se abre como
   * ruta muestreada por waypoints en lugar de pin; el resto de proveedores
   * no soportan rutas por URL y conservan el pin de `value`.
   */
  ruta?: LatLng[] | undefined;
  /** Items extra del menú (p. ej. descargar el KMZ para abrirlo en Google Earth). */
  children?: React.ReactNode;
  className?: string;
}

/**
 * Botón que abre una ubicación en aplicaciones de mapas externas: despliega
 * un menú con los proveedores soportados y una acción para copiar las
 * coordenadas. Con `ruta`, Google Maps recibe el trazado completo muestreado
 * (ver `construirUrlRutaGoogle`); los demás proveedores solo aceptan un punto.
 */
export function AbrirEnMapasButton({ value, ruta, children, className }: AbrirEnMapasButtonProps) {
  const rutaValida = ruta && ruta.length >= 2 ? ruta : null;

  const copiarCoordenadas = async () => {
    const texto = `${value.lat.toFixed(6)}, ${value.lng.toFixed(6)}`;
    try {
      await navigator.clipboard.writeText(texto);
      toast.success('Coordenadas copiadas al portapapeles.');
    } catch {
      toast.error('No se pudieron copiar las coordenadas.');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn('h-7 px-2.5 text-xs', className)}
        >
          <ExternalLink aria-hidden="true" className="size-3.5" />
          Abrir en otro mapa
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {rutaValida ? (
          <DropdownMenuItem asChild>
            <a
              href={construirUrlRutaGoogle(rutaValida)}
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer"
            >
              <Route aria-hidden="true" className="text-muted-foreground" />
              Google Maps (ruta aproximada)
            </a>
          </DropdownMenuItem>
        ) : null}
        {PROVEEDORES_MAPAS.filter((p) => !(rutaValida && p.id === 'google')).map((proveedor) => (
          <DropdownMenuItem key={proveedor.id} asChild>
            <a
              href={proveedor.construirUrl(value)}
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer"
            >
              <ExternalLink aria-hidden="true" className="text-muted-foreground" />
              {proveedor.nombre}
            </a>
          </DropdownMenuItem>
        ))}
        {children}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void copiarCoordenadas()} className="cursor-pointer">
          <Copy aria-hidden="true" className="text-muted-foreground" />
          Copiar coordenadas
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
